import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const TermsOfService = () => {
  return (
    <main className="min-h-screen bg-black py-20 px-6" data-testid="terms-page">
      <div className="max-w-4xl mx-auto">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-gray-400 hover:text-[#00FFD1] transition-colors mb-12"
          data-testid="back-link"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Home</span>
        </Link>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight mb-4">
            Terms of Service
          </h1>
          <p className="text-gray-400">Last updated: December 2025</p>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="prose prose-invert prose-lg max-w-none"
        >
          <div className="space-y-8 text-gray-300">
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">1. Agreement to Terms</h2>
              <p>
                By accessing or using EVOLVE's website and services ("Services"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">2. Description of Services</h2>
              <p>
                EVOLVE provides a fitness and wellness platform that connects users with local trainers, meal prep services, and wellness professionals. Our Services also include AI-powered nutrition tracking, calorie counting, workout logging, and progress monitoring tools.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">3. Eligibility</h2>
              <p>
                You must be at least 18 years old to use our Services. By using EVOLVE, you represent and warrant that you meet this age requirement and have the legal capacity to enter into these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">4. Account Registration</h2>
              <p>To access certain features of our Services, you may need to create an account. You agree to:</p>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and update your information as needed</li>
                <li>Keep your password secure and confidential</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>Notify us immediately of any unauthorized access</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">5. Acceptable Use</h2>
              <p>You agree not to:</p>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>Use the Services for any unlawful purpose</li>
                <li>Impersonate any person or entity</li>
                <li>Interfere with or disrupt the Services</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Upload viruses or malicious code</li>
                <li>Collect user information without consent</li>
                <li>Use the Services to send spam or unsolicited messages</li>
                <li>Violate any applicable laws or regulations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">6. Third-Party Service Providers</h2>
              <p>
                EVOLVE connects users with independent trainers, meal prep companies, and wellness professionals ("Service Providers"). You understand and agree that:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>Service Providers are independent contractors, not employees of EVOLVE</li>
                <li>EVOLVE does not guarantee the quality, safety, or legality of services provided</li>
                <li>Any agreements between you and Service Providers are solely between those parties</li>
                <li>You should verify credentials and qualifications of Service Providers</li>
                <li>EVOLVE is not responsible for any disputes between users and Service Providers</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">7. Health and Fitness Disclaimer</h2>
              <div className="p-6 bg-yellow-500/10 border border-yellow-500/20 rounded-xl">
                <p className="text-yellow-200">
                  <strong>Important:</strong> EVOLVE is not a medical service. The information and tools provided through our Services are for general informational and fitness purposes only. They are not intended to be a substitute for professional medical advice, diagnosis, or treatment.
                </p>
                <p className="mt-4 text-yellow-200">
                  Always consult with a qualified healthcare provider before starting any new fitness program, diet, or wellness routine. If you experience any health issues, seek medical attention immediately.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">8. AI-Powered Features</h2>
              <p>
                Our AI-powered nutrition tracking and recommendations are provided for convenience and general guidance. While we strive for accuracy, AI-generated information may contain errors. You should not rely solely on AI recommendations for health decisions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">9. Intellectual Property</h2>
              <p>
                All content, features, and functionality of the Services, including but not limited to text, graphics, logos, icons, images, and software, are the exclusive property of EVOLVE and are protected by copyright, trademark, and other intellectual property laws.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">10. User Content</h2>
              <p>
                You retain ownership of content you submit to EVOLVE. By submitting content, you grant us a non-exclusive, royalty-free, worldwide license to use, display, and distribute your content in connection with our Services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">11. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, EVOLVE shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or other intangible losses, resulting from:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>Your use or inability to use the Services</li>
                <li>Any conduct or content of third parties on the Services</li>
                <li>Unauthorized access to or alteration of your data</li>
                <li>Any other matter relating to the Services</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">12. Indemnification</h2>
              <p>
                You agree to indemnify and hold harmless EVOLVE, its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising out of your use of the Services or violation of these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">13. Termination</h2>
              <p>
                We may terminate or suspend your access to the Services at any time, without prior notice, for any reason, including breach of these Terms. Upon termination, your right to use the Services will immediately cease.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">14. Changes to Terms</h2>
              <p>
                We reserve the right to modify these Terms at any time. We will notify users of significant changes by posting the updated Terms on our website. Your continued use of the Services after changes constitutes acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">15. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to conflict of law principles.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">16. Contact Us</h2>
              <p>
                If you have any questions about these Terms of Service, please contact us at:
              </p>
              <div className="mt-4 p-6 bg-white/5 rounded-xl border border-white/10">
                <p><strong>EVOLVE Wellness</strong></p>
                <p className="mt-2">Email: legal@evolvewellnessapp.com</p>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </main>
  );
};

export default TermsOfService;
